document.addEventListener("DOMContentLoaded", () => {
  const collectionButtons = Array.from(
    document.querySelectorAll(".collection-button")
  );
  const collectionContent = document.querySelector(".collection-content");
  const prevButton = document.querySelector(".collection-arrow.prev");
  const nextButton = document.querySelector(".collection-arrow.next");
  

  if (!collectionContent || !collectionButtons.length) return;

  /* -----------------------------------------------------------
     Helpers
  ----------------------------------------------------------- */

  const handleExistsInSlider = (handle) =>
    collectionButtons.some(
      (button) => button.dataset.handle === handle
    );

  const getLanguagePrefix = () =>
    window.location.pathname.startsWith("/ar/") ? "/ar" : "";

  const getHandleFromPath = (pathname = window.location.pathname) => {
    const match = pathname.match(/^\/(ar\/)?collections\/([^\/]+)/);
    return match ? match[2] : null;
  };

  const parser = new DOMParser();

  const sliderState = {
    currentIndex: 0,
    visibleItems: Math.min(3, collectionButtons.length),
    isAnimating: false,
    isMobile: window.innerWidth < 1000,
    hasUserInteracted: false
  };

  const sliderContainer = collectionButtons[0]?.parentElement;

  const touchState = {
    attached: false,
    active: false,
    startX: 0,
    deltaX: 0,
    threshold: 30,
  };

  let pendingNavigation = null;

  const addBodyLoading = (flag) => {
    document.body.classList.toggle("collection-loading", flag);
  };

  const focusActiveButton = (handle) => {
    collectionButtons.forEach((button) => {
      button.classList.toggle(
        "active",
        button.dataset.handle === handle
      );
    });
  };

  const ensureMobileSliderCentered = (handle) => {
    if (!sliderState.isMobile) return;

    const activeIndex = collectionButtons.findIndex(
      (button) => button.dataset.handle === handle
    );

    if (activeIndex >= 0) {
      sliderState.currentIndex =
        (activeIndex -
          Math.floor(sliderState.visibleItems / 2) +
          collectionButtons.length) %
        collectionButtons.length;

      updateSliderTransforms();
    }
  };

  const updateSliderTransforms = () => {
    if (!sliderState.isMobile || sliderState.isAnimating) return;

    sliderState.isAnimating = true;

    const centerIndex =
      (sliderState.currentIndex +
        Math.floor(sliderState.visibleItems / 2)) %
      collectionButtons.length;

    collectionButtons.forEach((button, index) => {
      let position =
        (index - centerIndex + collectionButtons.length) %
        collectionButtons.length;

      if (position > collectionButtons.length / 2) {
        position -= collectionButtons.length;
      }

      const distance = Math.abs(position);

      if (!sliderState.hasUserInteracted) {
        // Only position, no scaling or fading
        button.style.transform = `translateX(${position * 110}%)`;
        button.style.opacity = "";
        button.style.zIndex = "";
      } else {
        button.style.transform = `translateX(${position * 110}%) scale(${1 -
          distance * 0.1})`;
        button.style.opacity = 1 - distance * 0.3;
        button.style.zIndex = sliderState.visibleItems - distance;
      }
    });

    setTimeout(() => {
      sliderState.isAnimating = false;
    }, 300);
  };

  const resetSliderTransforms = () => {
    collectionButtons.forEach((button) => {
      button.style.transform = "";
      button.style.opacity = "";
      button.style.zIndex = "";
    });
  };

  const fetchCollectionSection = async (url) => {
    const response = await fetch(`${url}&section_id=collection`);
    if (!response.ok) throw new Error("Failed to load section");

    const text = await response.text();
    const doc = parser.parseFromString(text, "text/html");
    const content = doc.querySelector(".collection-content");

    return content ? content.innerHTML : null;
  };

  const fetchCollectionByHandle = async (handle) => {
    const langPrefix = getLanguagePrefix();
    const url = `${langPrefix}/collections/${handle}`;

    const response = await fetch(`${url}?section_id=collection`);
    if (!response.ok) throw new Error("Failed to load collection");

    const text = await response.text();

    return {
      html: parser
        .parseFromString(text, "text/html")
        .querySelector(".collection-content")?.innerHTML,
      url,
      langPrefix,
    };
  };

  const setHistoryState = (handle, url, langPrefix, replace = false) => {
    const state = { handle, path: url, langPrefix };

    if (replace) {
      window.history.replaceState(state, "", url);
    } else {
      window.history.pushState(state, "", url);
    }
  };

  const renderCollectionContent = (html) => {
    if (!html) return;
    collectionContent.innerHTML = html;

    if (typeof initColorSwatches === "function") {
      initColorSwatches();
    }
    if (window.wishlist?.setInitialStates) {
      window.wishlist.setInitialStates();
    }
  };

  const loadCollection = async (
    handle,
    { skipHistory = false } = {}
  ) => {
    if (!handle) return;

    addBodyLoading(true);

    if (handleExistsInSlider(handle)) {
      focusActiveButton(handle);
      ensureMobileSliderCentered(handle);
    }

    try {
      const { html, url, langPrefix } =
        await fetchCollectionByHandle(handle);

      renderCollectionContent(html);

      if (!skipHistory) {
        setHistoryState(handle, url, langPrefix);
      }
    } catch (error) {
      console.error(error);
      window.location.href = `/collections/${handle}`;
    } finally {
      addBodyLoading(false);
    }
  };

  /* -----------------------------------------------------------
     Button Click
  ----------------------------------------------------------- */

  collectionButtons.forEach((button) => {
    button.addEventListener("click", (event) => {
      sliderState.hasUserInteracted = true;
      event.preventDefault();
      const handle = button.dataset.handle;
      if (!handle || pendingNavigation) return;

      pendingNavigation = loadCollection(handle).finally(() => {
        pendingNavigation = null;
      });
    });
  });

  /* -----------------------------------------------------------
     Pagination
  ----------------------------------------------------------- */

  document.addEventListener("click", async (event) => {
    const paginationLink = event.target.closest(
      ".pagination-link:not(.disabled)"
    );
    if (!paginationLink) return;

    event.preventDefault();
    const url = new URL(paginationLink.href);

    addBodyLoading(true);

    try {
      const html = await fetchCollectionSection(url.href);

      if (html) {
        renderCollectionContent(html);
        window.history.pushState(
          { path: url.href },
          "",
          url.href
        );

        const handle = getHandleFromPath(url.pathname);

        if (handleExistsInSlider(handle)) {
          focusActiveButton(handle);
          ensureMobileSliderCentered(handle);
        }
      } else {
        window.location.href = url.href;
      }
    } catch (error) {
      window.location.href = url.href;
    } finally {
      addBodyLoading(false);
    }
  });

  /* -----------------------------------------------------------
     Back / Forward
  ----------------------------------------------------------- */

  window.addEventListener("popstate", async (event) => {
    addBodyLoading(true);

    try {
      const handle =
        event.state?.handle || getHandleFromPath();

      const html = await fetchCollectionSection(
        window.location.href
      );

      renderCollectionContent(html);

      if (handleExistsInSlider(handle)) {
        focusActiveButton(handle);
        ensureMobileSliderCentered(handle);
      }
    } catch (error) {
      window.location.reload();
    } finally {
      addBodyLoading(false);
    }
  });

  /* -----------------------------------------------------------
     Initial Setup
  ----------------------------------------------------------- */

  const initialHandle = getHandleFromPath();
  const exists = handleExistsInSlider(initialHandle);

  if (exists) {
    focusActiveButton(initialHandle);

    if (sliderState.isMobile) {
      ensureMobileSliderCentered(initialHandle);
    }

    setHistoryState(
      initialHandle,
      window.location.href,
      getLanguagePrefix(),
      !window.history.state
    );
  } else {
    // Remove any active class
    collectionButtons.forEach((button) =>
      button.classList.remove("active")
    );

    // IMPORTANT: do NOT center anything
    if (sliderState.isMobile) {
      resetSliderTransforms();
    }
  }

  /* -----------------------------------------------------------
   Arrows
----------------------------------------------------------- */

  if (prevButton) {
    prevButton.addEventListener("click", () => {
      if (!sliderState.isMobile || sliderState.isAnimating) return;

      sliderState.hasUserInteracted = true;

      sliderState.currentIndex =
        (sliderState.currentIndex - 1 + collectionButtons.length) %
        collectionButtons.length;

      updateSliderTransforms();
    });
  }

  if (nextButton) {
    nextButton.addEventListener("click", () => {
      if (!sliderState.isMobile || sliderState.isAnimating) return;

      sliderState.hasUserInteracted = true;

      sliderState.currentIndex =
        (sliderState.currentIndex + 1) %
        collectionButtons.length;

      updateSliderTransforms();
    });
  }

  if (!sliderState.isMobile) {
    resetSliderTransforms();
  } else {
    updateSliderTransforms();
  }
});